import CloudSyncIcon from '@mui/icons-material/CloudSync';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Typography
} from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'delivered':
      return 'success';
    case 'in transit':
    case 'picked up':
    case 'arriving':
      return 'primary';
    case 'exception':
    case 'delayed':
      return 'error';
    case 'out for delivery':
      return 'warning';
    default:
      return 'default';
  }
};

function TrackingCard({ record, linkTo }) {
  return (
    <Card elevation={2} sx={{ borderRadius: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              <Chip
                label={record.Status || 'No Status'}
                color={getStatusColor(record.Status)}
                size="small"
                sx={{ mr: 1 }}
              />
              {record.ZoeShipmentTrackingId
                ? `ID: ${record.ZoeShipmentTrackingId}`
                : record.TrackingNumber || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(record.CreatedDate || record.ShipmentDate)}
            </Typography>
          </Box>
          {linkTo && (
            <Link
              component={RouterLink}
              to={linkTo}
              sx={{ fontSize: '0.875rem' }}
            >
              View Details
            </Link>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Tracking Number
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {record.TrackingNumber || 'N/A'}
            </Typography>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Order Number
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {record.OrderNumber || 'N/A'}
            </Typography>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Source
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {record.Source || 'N/A'}
            </Typography>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Carrier Name
            </Typography>
            <Typography variant="body2">
              {record.CarrierName || 'N/A'}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Service Name
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {record.ServiceName || 'N/A'}
            </Typography>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Shipment Date
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {formatDate(record.ShipmentDate)}
            </Typography>

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.secondary' }}
            >
              Last Updated
            </Typography>
            <Typography variant="body2">
              {formatDate(record.LastUpdated)}
            </Typography>
          </Box>
        </Box>

        {(record.ShipFromAddress || record.ShipToAddress) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2
              }}
            >
              {record.ShipFromAddress && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: 'text.secondary' }}
                  >
                    Ship From
                  </Typography>
                  <Typography variant="body2">
                    {record.ShipFromAddress}
                  </Typography>
                </Box>
              )}
              {record.ShipToAddress && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: 'text.secondary' }}
                  >
                    Ship To
                  </Typography>
                  <Typography variant="body2">
                    {record.ShipToAddress}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {record.Note && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}
              >
                Notes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  backgroundColor: 'grey.50',
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                {record.Note}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function PurchaseOrderTrackingTab({
  trackingRecords,
  odataRecords,
  odataLoading,
  odataSearched,
  odataOrderNumber,
  onSearchOdata
}) {
  const hasDbRecords = trackingRecords && trackingRecords.length > 0;
  const hasOdataRecords = odataRecords && odataRecords.length > 0;

  return (
    <Box>
      {/* ── DB Section ── */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Shipment Tracking
        {hasDbRecords && (
          <Chip
            label={`${trackingRecords.length} ${trackingRecords.length === 1 ? 'record' : 'records'}`}
            size="small"
            color="primary"
            sx={{ ml: 1, fontWeight: 500 }}
          />
        )}
      </Typography>

      {hasDbRecords ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          {trackingRecords.map((record) => (
            <TrackingCard
              key={record.ZoeShipmentTrackingId}
              record={record}
              linkTo={`/zoe-shipment-tracking/${record.ZoeShipmentTrackingId}`}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            border: '1px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            mb: 4
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No local tracking records found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No matching records in the database for this Purchase Order&apos;s
            POTitle.
          </Typography>
        </Box>
      )}

      {/* ── OData Section ── */}
      <Divider sx={{ mb: 3 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            External Tracking
            {odataOrderNumber && (
              <Chip
                label={`Order # ${odataOrderNumber}`}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
            {hasOdataRecords && (
              <Chip
                label={`${odataRecords.length} ${odataRecords.length === 1 ? 'result' : 'results'}`}
                size="small"
                color="secondary"
                sx={{ ml: 1, fontWeight: 500 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Search the ZoeShipmentTracking external API by OrderNumber derived
            from POTitle.
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="secondary"
          startIcon={
            odataLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CloudSyncIcon />
            )
          }
          onClick={onSearchOdata}
          disabled={odataLoading}
        >
          {odataLoading
            ? 'Searching…'
            : odataSearched
              ? 'Search Again'
              : 'Search External'}
        </Button>
      </Box>

      {odataSearched && !odataLoading && (
        <>
          {hasOdataRecords ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {odataRecords.map((record, index) => (
                <TrackingCard
                  key={record.ZoeShipmentTrackingId ?? index}
                  record={record}
                  linkTo={
                    record.ZoeShipmentTrackingId
                      ? `/zoe-shipment-tracking/${record.ZoeShipmentTrackingId}`
                      : null
                  }
                />
              ))}
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No external tracking records found
              {odataOrderNumber
                ? ` for order number "${odataOrderNumber}"`
                : ''}
              .
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
